import { useNavigate } from '@fukuri-admins/hooks/common';
import { MailIcon, Table, TableCell, TableRow } from '@fukuri-admins/ui/common';
import { useState } from 'react';
import { sendReminder } from '~/api/users/id/login_reminders';
import { useAuth } from '~/components/auth/AuthProvider';
import MessageModal from '~/components/common/MessageModal';
import { SendReminderModal } from '~/components/User/SendReminderModal';
import { ROUTES, replaceRouteParams } from '~/utils/routes';
import styles from './styles.module.scss';

type PendingUserRow = {
  no: number;
  id: string;
  full_name: string;
  email: string;
  created_at: string;
  account_information_sent_at: string;
};

type Props = {
  rows: PendingUserRow[];
  onSort: (column: string) => void;
  /** true の場合、行クリックによる /users/:id への遷移を無効化する */
  disableRowNavigation?: boolean;
};

export const PendingUserTableContent = ({ rows, onSort, disableRowNavigation = false }: Props) => {
  const [isReminderModalOpen, setIsReminderModalOpen] = useState(false);
  const [errMsg, setErrMsg] = useState('');
  const [isErrorMessageModalOpen, setIsErrorMessageModalOpen] = useState(false);
  const [isCompleteModalOpen, setIsCompleteModalOpen] = useState(false);
  const [remindTargetRow, setRemindTargetRow] = useState<PendingUserRow | null>(null);
  const { signOut } = useAuth();
  const navigate = useNavigate();

  const handleSendReminderConfirm = async () => {
    try {
      await sendReminder(signOut, remindTargetRow.id);
      setIsReminderModalOpen(false);
      setRemindTargetRow(null);
      setIsCompleteModalOpen(true);
    } catch (error) {
      setErrMsg(error.message);
      setIsErrorMessageModalOpen(true);
      setIsReminderModalOpen(false);
      setRemindTargetRow(null);
    }
  };

  if (!rows || rows.length === 0) {
    return <div className={styles.emptyTable}>データなし</div>;
  }

  return (
    <>
      <Table
        fixedHeader
        maxHeight='min(720px, 90vh)'
        headers={[
          { label: 'No', sortable: true, onSort: () => onSort('no') },
          { label: '名前', sortable: true, onSort: () => onSort('full_name') },
          { label: 'メールアドレス', sortable: true, onSort: () => onSort('email') },
          { label: '作成日時', sortable: true, onSort: () => onSort('created_at') },
          {
            label: '招待日時',
            sortable: true,
            onSort: () => onSort('account_information_sent_at'),
          },
          { label: '再招待メール' },
        ]}
      >
        {rows.map(row => (
          <TableRow
            key={row.id}
            onClick={
              disableRowNavigation
                ? undefined
                : () => navigate(replaceRouteParams(ROUTES.USER_SHOW, { id: Number(row.id) }))
            }
          >
            <TableCell>{row.no}</TableCell>
            <TableCell>{row.full_name ?? '-'}</TableCell>
            <TableCell>{row.email ?? '-'}</TableCell>
            <TableCell>{row.created_at ?? '-'}</TableCell>
            <TableCell>{row.account_information_sent_at ?? '-'}</TableCell>
            <TableCell>
              <div className={styles.action}>
                <button
                  type='button'
                  className={styles.actionIcon}
                  onClick={e => {
                    e.stopPropagation();
                    setRemindTargetRow(row);
                    setIsReminderModalOpen(true);
                  }}
                >
                  <MailIcon />
                  送信
                </button>
              </div>
            </TableCell>
          </TableRow>
        ))}
      </Table>
      <SendReminderModal
        fullName={remindTargetRow?.full_name}
        isModalOpen={isReminderModalOpen && remindTargetRow !== null}
        onClose={() => {
          setIsReminderModalOpen(false);
          setRemindTargetRow(null);
        }}
        action={handleSendReminderConfirm}
      />
      <MessageModal
        title='エラー'
        description={`エラーが発生しました。\n\n<<エラーメッセージ>>\n${errMsg}`}
        show={isErrorMessageModalOpen}
        onClose={() => setIsErrorMessageModalOpen(false)}
      />
      <MessageModal
        title='送信完了'
        description={'メールの送信が完了しました。'}
        show={isCompleteModalOpen}
        onClose={() => setIsCompleteModalOpen(false)}
      />
    </>
  );
};
